import type { SearchHighlightTextRes } from '@/api/gen/_model/searchHighlightTextRes';

const HIT_TYPE = 'hit';
const TEXT_TYPE = 'text';

const hasHitSegment = (segments: readonly SearchHighlightTextRes[]) =>
  segments.some(
    segment => segment.type === HIT_TYPE && segment.value.length > 0,
  );

/**
 * Atlas Search highlights are snippets, not a replacement for the original field.
 * Build renderable segments from the original source text and use server-provided
 * highlight segments only as decoration. This preserves unmatched prefixes,
 * suffixes, and gaps when Atlas returns partial nGram highlight fragments.
 */
export const buildSourceBackedHighlightSegments = (
  sourceText: string,
  highlightSegments?: readonly SearchHighlightTextRes[],
): SearchHighlightTextRes[] | null => {
  if (!highlightSegments || !hasHitSegment(highlightSegments)) return null;

  const sourceBackedSegments: SearchHighlightTextRes[] = [];
  let cursor = 0;

  for (const segment of highlightSegments) {
    const value = segment.value;
    if (!value) continue;

    const matchIndex = sourceText.indexOf(value, cursor);
    if (matchIndex < 0) {
      return null;
    }

    if (matchIndex > cursor) {
      sourceBackedSegments.push({
        value: sourceText.slice(cursor, matchIndex),
        type: TEXT_TYPE,
      });
    }

    sourceBackedSegments.push({
      value: sourceText.slice(matchIndex, matchIndex + value.length),
      type: segment.type,
    });
    cursor = matchIndex + value.length;
  }

  if (cursor < sourceText.length) {
    sourceBackedSegments.push({
      value: sourceText.slice(cursor),
      type: TEXT_TYPE,
    });
  }

  return sourceBackedSegments.length > 0 ? sourceBackedSegments : null;
};

export const buildSourceBackedHitTermSegments = (
  sourceText: string,
  hitTerms?: readonly string[],
): SearchHighlightTextRes[] | null => {
  const ranges = hitTerms
    ?.flatMap(term => {
      if (term.length === 0) return [];

      const termRanges: Array<{ start: number; end: number }> = [];
      let cursor = 0;

      while (cursor < sourceText.length) {
        const start = sourceText.indexOf(term, cursor);
        if (start < 0) break;

        const end = start + term.length;
        termRanges.push({ start, end });
        cursor = end;
      }

      return termRanges;
    })
    .sort((a, b) => (a.start !== b.start ? a.start - b.start : b.end - a.end));

  if (!ranges || ranges.length === 0) return null;

  const mergedRanges: Array<{ start: number; end: number }> = [];
  for (const range of ranges) {
    const lastRange = mergedRanges.at(-1);
    if (lastRange && range.start <= lastRange.end) {
      lastRange.end = Math.max(lastRange.end, range.end);
      continue;
    }

    mergedRanges.push({ ...range });
  }

  const sourceBackedSegments: SearchHighlightTextRes[] = [];
  let cursor = 0;

  for (const range of mergedRanges) {
    if (range.start > cursor) {
      sourceBackedSegments.push({
        value: sourceText.slice(cursor, range.start),
        type: TEXT_TYPE,
      });
    }

    sourceBackedSegments.push({
      value: sourceText.slice(range.start, range.end),
      type: HIT_TYPE,
    });
    cursor = range.end;
  }

  if (cursor < sourceText.length) {
    sourceBackedSegments.push({
      value: sourceText.slice(cursor),
      type: TEXT_TYPE,
    });
  }

  return sourceBackedSegments;
};
